import { mergeTranslations } from "ra-core";
import polyglotI18nProvider from "ra-i18n-polyglot";
import englishMessages from "ra-language-english";
import frenchMessages from "ra-language-french";
import { raSupabaseEnglishMessages } from "ra-supabase-language-english";
import { raSupabaseFrenchMessages } from "ra-supabase-language-french";
import { englishCrmMessages } from "./englishCrmMessages";
import { frenchCrmMessages } from "./frenchCrmMessages";
import { germanCrmMessages } from "./germanCrmMessages";
import { germanRaMessages } from "./germanRaMessages";
import { spanishCrmMessages } from "./spanishCrmMessages";
import { spanishRaMessages } from "./spanishRaMessages";

const raSupabaseEnglishMessagesOverride = {
  "ra-supabase": {
    auth: {
      password_reset: "Check your emails for a Reset Password message.",
    },
  },
};

const raSupabaseFrenchMessagesOverride = {
  "ra-supabase": {
    auth: {
      password_reset:
        "Consultez vos emails pour trouver le message de reinitialisation du mot de passe.",
    },
  },
};

/**
 * German and Spanish have no usable ra-supabase-language-* package (and no
 * ra-language-* package for this react-admin version either — the published
 * ones are 3.13.5 / 1.0.0). The react-admin core catalogs live in
 * ./germanRaMessages and ./spanishRaMessages; the ra-supabase strings rendered
 * on the auth screens are maintained here.
 */
const raSupabaseGermanMessagesOverride = {
  "ra-supabase": {
    auth: {
      forgot_password: "Passwort vergessen?",
      missing_tokens: "Zugriffs- und Refresh-Token fehlen",
      password_reset:
        "Prüfen Sie Ihre E-Mails auf die Nachricht zum Zurücksetzen des Passworts.",
    },
    reset_password: {
      forgot_password: "Passwort vergessen?",
      forgot_password_details:
        "Geben Sie Ihre E-Mail-Adresse ein, um weitere Anweisungen zu erhalten.",
    },
    set_password: { new_password: "Wählen Sie Ihr Passwort" },
    validation: { password_mismatch: "Die Passwörter stimmen nicht überein" },
  },
};

const raSupabaseSpanishMessagesOverride = {
  "ra-supabase": {
    auth: {
      forgot_password: "¿Olvidó su contraseña?",
      missing_tokens: "Faltan los tokens de acceso y de actualización",
      password_reset:
        "Revise su correo electrónico para encontrar el mensaje de restablecimiento de contraseña.",
    },
    reset_password: {
      forgot_password: "¿Olvidó su contraseña?",
      forgot_password_details:
        "Introduzca su correo electrónico para recibir instrucciones.",
    },
    set_password: { new_password: "Elija su contraseña" },
    validation: { password_mismatch: "Las contraseñas no coinciden" },
  },
};

const englishCatalog = mergeTranslations(
  englishMessages,
  raSupabaseEnglishMessages,
  raSupabaseEnglishMessagesOverride,
  englishCrmMessages,
);

const frenchCatalog = mergeTranslations(
  englishCatalog,
  frenchMessages,
  raSupabaseFrenchMessages,
  raSupabaseFrenchMessagesOverride,
  frenchCrmMessages,
);

const germanCatalog = mergeTranslations(
  englishCatalog,
  germanRaMessages,
  raSupabaseGermanMessagesOverride,
  germanCrmMessages,
);

const spanishCatalog = mergeTranslations(
  englishCatalog,
  spanishRaMessages,
  raSupabaseSpanishMessagesOverride,
  spanishCrmMessages,
);

export const supportedLocales = ["en", "fr", "de", "es"] as const;

export type SupportedLocale = (typeof supportedLocales)[number];

const catalogs: Record<SupportedLocale, typeof englishCatalog> = {
  en: englishCatalog,
  fr: frenchCatalog,
  de: germanCatalog,
  es: spanishCatalog,
};

export const getInitialLocale = (): SupportedLocale => {
  if (typeof navigator === "undefined") {
    return "en";
  }

  const browserLocale = (
    navigator.languages?.[0] ?? navigator.language
  )?.toLowerCase();
  if (!browserLocale) {
    return "en";
  }

  return (
    supportedLocales.find((locale) => browserLocale.startsWith(locale)) ?? "en"
  );
};

export const i18nProvider = polyglotI18nProvider(
  (locale) => catalogs[locale as SupportedLocale] ?? englishCatalog,
  getInitialLocale(),
  [
    { locale: "en", name: "English" },
    { locale: "fr", name: "Français" },
    { locale: "de", name: "Deutsch" },
    { locale: "es", name: "Español" },
  ],
  { allowMissing: true },
);

export const testI18nProvider = polyglotI18nProvider(
  () => englishCatalog,
  "en",
  [{ locale: "en", name: "English" }],
  { allowMissing: true },
);
